import { randomIntFromInterval } from '../util/math';

export const DEFAULT_FILENAME = 'mv-rt.dcm';
export const DEFAULT_INTERPRETED_TYPE = 'ORGAN';

/** Name of 'default' base customization */
export const DEFAULT_MODEL_CUSTOMIZATION_NAME = 'default';

/** Model type for segmentation models. */
export const SEGMENTATION_MODEL_TYPE = 'segmentation';

/** Name of the filename metadata attribute. */
export const METADATA_FILENAME_ATTRIBUTE = 'filename';

/** The 'action' trigger used when a trigger is a DICOM rule instead of an AE Title rule. */
export const DICOM_RULE_TRIGGER_ACTION = 'seg';

export const DEFAULT_SUBSCRIPTION = 'default';

/** One of currently two supported physical properties: REL_ELEC_DENSITY */
export const PHYSICAL_PROPERTY_REL_ELEC_DENSITY = 'REL_ELEC_DENSITY';

/** One of currently two supported physical properties: REL_MASS_DENSITY */
export const PHYSICAL_PROPERTY_REL_MASS_DENSITY = 'REL_MASS_DENSITY';

export const SUPPORTED_PHYSICAL_PROPERTIES = [PHYSICAL_PROPERTY_REL_ELEC_DENSITY, PHYSICAL_PROPERTY_REL_MASS_DENSITY];

// validation messages
export const VALIDATION_MESSAGE_AE_TITLES_MUST_BE_UNIQUE = 'Action names in AE Title rules must be unique.';

export enum ModelSelectionScope { None, ModelRules, AeTitleRule, DicomRule, DicomAttributeRule, All };

/** Currently supported physical property attribute names. TODO: use the const assignments above in these when we update to TypeScript 5.0+ */
export enum PhysicalProperty { NotSet = 'NotSet', RelElecDensity = 'REL_ELEC_DENSITY', RelMassDensity = 'REL_MASS_DENSITY' };

/** Visibility of a segmentation model */
export enum ModelVisibility {
    Default = 'DefaultVisibility',
    AlwaysShown = 'AlwaysShown',
    AlwaysHidden = 'AlwaysHidden'
}

/** Parent type for modelling a segmentation model and customization and selection/trigger rules that are related to it. */
export type SegmentationModel = {
    /** Redux store ID of this model. */
    id: string;
    /** Name of the segmentation model. */
    modelName: string;
    /** Human-friendly label of the segmentation model. */
    label: string;
    /** Body part that this segmentation model covers. */
    bodyPart: string;
    /** An optional description of the segmentation model. */
    description: string;
    /** Visibility of this model: default, always shown, or always hidden. */
    visibility: ModelVisibility;

    /** Customizations and outputs that belong to this segmentation model. */
    customizations: string[]; // ModelCustomizationBase ids

    // TODO: add model_type support once types other than 'seg' are supported

    /** True if this segmentation model has unsaved changes, false otherwise. */
    isModified: boolean;

    // True if this model is available for user to use in the actual segmentation product, false otherwise.
    isAvailable: boolean;

    //True if model is in the deprecated list, false otherwise.
    isDeprecated: boolean;
};

/** A single model customization. One model customization can end up having multiple outputs (ie. result files). */
export type ModelCustomizationBase = {
    /** Redux store ID of this customization base. */
    id: string;
    /** Name for this customization. */
    customizationName: string;
    /** An optional description of this customization base. */
    description: string;

    /** One or more outputs (ie. result files) that belong to this customization base. */
    outputs: string[]; // ModelCustomizationOutput ids

    /** Selection rules that trigger this customization. */
    aeTitleRules: string[];  // AeTitleRule ids
    dicomRules: string[];  // DicomRule ids

    /** Parent segmentation model ID of this customization base. */
    segmentationModelId: string;

    /** True if this customization base has unsaved changes, false otherwise. */
    isModified: boolean;
};

/** A single customization output (ie. a result file). A customization base contains one or more of these. */
export type ModelCustomizationOutput = {
    /** Redux store ID of this customization output. */
    id: string;
    /** Shorthand access to this output's filename metadata entry (an empty string if not defined). This is only used
     * for user-facing UI purposes -- the matching metadata entry is still the single source of truth for this attribute. */
    filename: string;

    /** Customization metadata relevant to this customization output. */
    metadata: string[]; // ModelCustomizationMetadataItem ids
    /** ROI customizations that belong to this customization output. */
    rois: string[]; // RoiCustomization ids

    /** Parent customization base ID of this output. */
    modelCustomizationBaseId: string;

    /** True if this customization output has unsaved changes, false otherwise. */
    isModified: boolean;
};

/** A single customization metadata item. */
export type ModelCustomizationMetadataItem = {
    /** Redux ID of this customization metadata item. */
    id: string;
    /** Name of the metadata attribute. */
    attribute: string;
    /** Value of the metadata attribute. */
    value: string;

    /** True if this metadata item has unsaved changes, false otherwise. */
    isModified: boolean;
    /** If true, scroll the viewport to show this item when it's mounted into DOM. This is used to auto-scroll to newly added items. */
    scrollToView: boolean;

    /** Parent model customization output this metadata item belongs to. */
    modelCustomizationOutputId?: string;   // this is optional because it's usually set post-constructor
}

/** Utility type for FMA IDs. */
export type FmaId = {
    hasFmaId: boolean;
    fmaIdCodeValue?: string;
    fmaIdCodeMeaning?: string;
    fmaIdCodingSchemeDesignator?: string;
    fmaIdCodingSchemeVersion?: string;
    fmaIdMappingResource?: string;
    fmaIdContextGroupVersion?: string;
    fmaIdContextIdentifier?: string;
    fmaIdContextUid?: string;
    fmaIdMappingResourceUid?: string;
    fmaIdMappingResourceName?: string;
    modelCustomizationOutputId?: string;
};

export type FmaIdKeys = keyof FmaId;

/** Customization settings for a single ROI. */
export type RoiCustomization = FmaId & {
    /** Redux ID of this roi customization. */
    id: string;
    /** The simple ROI or a more complex ROI operation that this customization applies to. */
    operation: string;
    /** The name of the output ROI. This can be used to change the name of the ROI from its default, or to create ROIs with new names based on a more complex ROI operation. */
    name: string;
    /** Contour color of the output ROI. RGB, 0-255. */
    color: [number, number, number];
    /** Interpreted ROI type of the output ROI.  */
    interpretedType: string;
    /** True if the output ROI should be included in its related customization output, false if it should be omitted. */
    isIncluded: boolean;
    /** Built-in ROIs cannot be deleted or their operations be changed. */
    isBuiltInRoi: boolean;

    /** Single physical property attribute. TODO: support multiple of these by making a new Entity Adapter. */
    physicalPropertyAttribute: PhysicalProperty;
    /** Value for the single physical property attribute. */
    physicalPropertyValue: string;


    /** True if this roi customization has unsaved changes, false otherwise. */
    isModified: boolean;
    /** If true, scroll the viewport to show this item when it's mounted into DOM. This is used to auto-scroll to newly added items. */
    scrollToView: boolean;

    /** Parent model customization output this roi customization belongs to. */
    modelCustomizationOutputId?: string;  // this is optional because it's usually set post-constructor
    /** Parent global roi customization this roi customization belongs to, if any. */
    globalRoiId?: string | undefined;  // this is optional because it's usually set post-constructor
};

/** Base type for a model selection/trigger rule. */
type RuleBase = {
    /** Redux store ID for this rule. */
    id: string;

    /** Name of the subscription that this rule belongs to. */
    subscription: string;

    /** The model customization that this rule will trigger. This should match whatever the customizationName is set to. */
    modelCustomizationBaseId?: string;  // this is optional because it's usually set post-constructor

    /** If true this rule can be edited by users. If false it's considered built-in and cannot be adjusted. */
    isEditable: boolean;
    /** If true this rule is highlighted in UI as being "new". */
    isNew: boolean;
    /** True if this rule has unsaved changes, false otherwise. */
    isModified: boolean;
    /** True if this rule has passed validation, false otherwise. */
    isValid: boolean;
    /** Validation message, if any. Undefined if validation has passed. */
    validationMessage: string | undefined;
}

/** Model selection/trigger rule that is triggered from a specific AE Title or segmentation backend API value. */
export type AeTitleRule = RuleBase & {
    /** Value of the AE Title or segmentation backend API action field that will trigger this rule. */
    action: string;
}

/** Model selection/trigger rule that is triggered from specific DICOM attribute values. */
export type DicomRule = RuleBase & {
    /** List of DICOM attributes and values that, on match, will trigger this rule. */
    dicomAttributes: string[]; // DicomAttributeRule ids
}

/** A DICOM attribute name and value set that can be used to trigger a DICOM rule. */
export type DicomAttributeRule = {
    /** Redux store ID of this DICOM attribute rule. */
    id: string;
    /** Name of the matching DICOM attribute that will trigger this rule. */
    attribute: string;
    /** Value (either direct or regexed) of the matching DICOM attribute that will trigger this rule. */
    value: string;
    /** Id of the DICOM rule this attribute belongs to. */
    parentDicomRuleId: string;

    /** If true this rule is highlighted in UI as being "new". */
    isNew: boolean,
    /** True if this rule has unsaved changes, false otherwise. */
    isModified: boolean;
    /** True if this rule has passed validation, false otherwise. */
    isValid: boolean;
}

/** A model can be triggered by one or more AE Title rules and/or DICOM rules. */
export type ModelTrigger = AeTitleRule | DicomRule;



export type GlobalRoiCustomization = FmaId & {
    id: string;
    operation: string;  // NOTE: global rois are grouped primarily by operation (but of course all values must match)
    name: string;
    isIncluded: boolean; // TODO: should this be here?
    color: [number, number, number];
    isModified: boolean;
    interpretedType: string;

    /** Single physical property attribute. TODO: support multiple of these by making a new Entity Adapter. */
    physicalPropertyAttribute: PhysicalProperty;
    /** Value for the single physical property attribute. */
    physicalPropertyValue: string;

    /** built-in ROIs cannot be deleted or their operations be changed. a global roi is a built-in ROI if ANY of the rois its covers
     * has canBeDeleted set to false. */
    isBuiltInRoi: boolean;

    /** IDs of RoiCustomization entities that are covered under this global customization entry. */
    coveredRois: string[];

    /** IDs of RoiCustomization entities that are exempt from this global customization, and have their own unique customizations. */
    excludedRois: string[];
}

export type ModelCustomizationEntities = {
    models: SegmentationModel[];
    customizationBases: ModelCustomizationBase[];
    customizationOutputs: ModelCustomizationOutput[];
    modelCustomizationsMetadata: ModelCustomizationMetadataItem[];
    roiCustomizations: RoiCustomization[];
    globalRoiCustomizations: GlobalRoiCustomization[];
    aeTitleRules: AeTitleRule[];
    dicomRules: DicomRule[];
    dicomAttributeRules: DicomAttributeRule[];
};

export type ModelCustomizationEntitiesForExport = {
    models: SegmentationModel[];
    customizationBases: ModelCustomizationBase[];
    customizationOutputs: ModelCustomizationOutput[];
    modelCustomizationsMetadata: ModelCustomizationMetadataItem[];
    roiCustomizations: RoiCustomization[];
    aeTitleRules: AeTitleRule[];
    dicomRules: DicomRule[];
    dicomAttributeRules: DicomAttributeRule[];
};

/** View model entities related to customization output */
export type ModelCustomizationOutputEntities = {
    customizationOutputs: ModelCustomizationOutput[];
    modelCustomizationsMetadata: ModelCustomizationMetadataItem[];
    roiCustomizations: RoiCustomization[];
};

/** Type of a model customization view model object. For helper function use only. */
export enum CustomizationObjectType {
    None = "NONE",
    Model = "MODEL",
    CustomizationBase = "CUSTOMIZATION_BASE",
    CustomizationOutput = "CUSTOMIZATION_OUTPUT",
    Metadata = "METADATA",
    Roi = "ROI",
    GlobalRoi = "GLOBAL_ROI",
    AeTitleRule = "AE_TITLE_RULE",
    DicomRule = "DICOM_RULE",
    DicomAttributeRule = "DICOM_ATTRIBUTE_RULE",
    TriggerRule = "TRIGGER_RULE",
    FmaId = "FMA_ID",

    /** TODO: this is currently used ONLY for reading backend error messages */
    PhysicalProperties = 'PHYSICAL_PROPERTIES',
};

/** Helper type for constructing hierarchical tree views from customization data. Since these objects are
 * recursive and not flat it's best to avoid storing them in redux store.
 */
export type CustomizationTreeViewDataItem = {
    /** ID of this item. */
    id: string,
    /** Human-readable label of this item. */
    label: string,
    /** Recursive children of this item. */
    children: CustomizationTreeViewDataItem[],
    /** Soft link to parent object */
    parentId: string | undefined,
    /** The type that this item models. */
    type: CustomizationObjectType,
    // /** If defined, this ID will be used instead of this object's actual ID when performing actions on this item (e.g. "opening" it in UI).
    //  * Should match a given actionType. */
    // actionId?: string,
    // /** If defined, this CustomizationObjectType will be used instead of this object's actual type when performing actions on this item
    //  *  (e.g. "opening" it in UI). Should match a given actionId. */
    // actionType?: CustomizationObjectType,
    isModified: boolean,
    /** True if this object and all its descendants are valid, false if this or any of its descendants have validation errors. */
    isValid: boolean,
    /** True if this object is available for user use in the actual product, false otherwise. */
    isAvailable: boolean,
    /** True if this object is in the deprecated list, false otherwise. */
    isDeprecated: boolean,
    /** Model visibility of this item. Mainly applicable for models, other customization object types are not guaranteed to follow this visibility setting. */
    modelVisibility: ModelVisibility,
}


// *** type guards ***

export const isSegmentationModel = (obj: any): obj is SegmentationModel => {
    const model = obj as SegmentationModel;
    return model.modelName !== undefined && model.customizations !== undefined;
}

export const isModelCustomizationBase = (obj: any): obj is ModelCustomizationBase => {
    const customization = obj as ModelCustomizationBase;
    return customization.customizationName !== undefined && customization.outputs !== undefined
        && customization.aeTitleRules !== undefined && customization.dicomRules !== undefined
        && customization.segmentationModelId !== undefined;
}

export const isModelCustomizationOutput = (obj: any): obj is ModelCustomizationOutput => {
    const output = obj as ModelCustomizationOutput;
    return output.metadata !== undefined && output.rois !== undefined && output.modelCustomizationBaseId !== undefined
        && output.filename !== undefined;
}

export const isModelCustomizationMetadata = (obj: any): obj is ModelCustomizationMetadataItem => {
    const metadata = obj as ModelCustomizationMetadataItem;

    // ensure that the metadata object does NOT have the parentDicomRuleId as otherwise metadata and dicom attribute rule types are very similar
    return metadata.attribute !== undefined && metadata.value !== undefined && (obj as DicomAttributeRule).parentDicomRuleId === undefined;
}

export const isRoiCustomization = (obj: any): obj is RoiCustomization => {
    const roi = obj as RoiCustomization;
    return roi.operation !== undefined && roi.color !== undefined && (obj as GlobalRoiCustomization).coveredRois === undefined;
}

export const isGlobalRoiCustomization = (obj: any): obj is GlobalRoiCustomization => {
    const globalRoi = obj as GlobalRoiCustomization;
    return globalRoi.operation !== undefined && globalRoi.color !== undefined && globalRoi.coveredRois !== undefined && globalRoi.interpretedType !== undefined;
}

const isRuleBase = (obj: any): obj is RuleBase => {
    const ruleBase = obj as RuleBase;
    return ruleBase.subscription !== undefined;
}

export const isAeTitleRule = (obj: any): obj is AeTitleRule => {
    const aeTitleRule = obj as AeTitleRule;
    return isRuleBase(obj) && aeTitleRule.action !== undefined && (obj as DicomRule).dicomAttributes === undefined;
}

export const isDicomRule = (obj: any): obj is DicomRule => {
    const dicomRule = obj as DicomRule;
    return isRuleBase(obj) && dicomRule.dicomAttributes !== undefined;
}

export const isDicomAttributeRule = (obj: any): obj is DicomAttributeRule => {
    const dicomAttributeRule = obj as DicomAttributeRule;
    return dicomAttributeRule.attribute !== undefined && dicomAttributeRule.value !== undefined && dicomAttributeRule.parentDicomRuleId !== undefined;
}


// export const generateRoiCustomizationHash = (roi: RoiCustomization | GlobalRoiCustomization): string => {
//     // only these keys are used for checking similarity
//     const includedKeys = ['operation', 'name', 'color'];
//     return hash(roi, { excludeKeys: (key) => !includedKeys.includes(key) });
// }


export const generateRandomColor = (): [number, number, number] => {
    return [randomIntFromInterval(0, 255), randomIntFromInterval(0, 255), randomIntFromInterval(0, 255)];
}
